import React, { useState } from "react";
import {
  List,
  ListItemButton,
  Box,
  IconButton,
  InputBase,
  Collapse,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import { Folder, FolderOpen } from "@mui/icons-material";
import SettingsOutlined from "@mui/icons-material/SettingsOutlined";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import axios from "axios";
import { API_URL } from "../../consts";
import { useDispatch } from "react-redux";

// Import modern sidebar typography styles
import './SidebarTypography.css';

const SpaceList = ({
  spaces,
  open,
  location,
  navigate,
  isMobile,
  setMobileOpen,
  setProfile,
  expandedSpaces,
  handleSpaceToggle,
  draggingSpaceId,
  setDraggingSpaceId,
  draggingSpace,
  setDraggingSpace,
  dragInProcess,
  setDragInProcess,
  dropDestinationId,
  setDropDestinationId,
  dropDestinationSpace,
  setDropDestinationSpace,
  setShowActionDialog,
  handleCollaboratorsOpen,
  setIsCreateSpaceOpen,
  setIsSubSpaceMode,
  disableDrag = false,
  hideSearch = false,
  isCurrentSpace = false,
  showSearch = false,
  loadingSubspaces = {},
  setParentSpaceId,
  onSpacesUpdate,
  allowRatedObjectDrop = false,
  showSpacePopup,
  organizationId = null, // When set, fetches user's spaces within this organization
  orgSharedMode = false, // When true with organizationId, searches shared spaces within org
  orgAllCollectionsMode = false, // When true with organizationId, searches all org collections via org API
}) => {
  const dispatch = useDispatch();
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [dragOverSpaceId, setDragOverSpaceId] = useState(null);

  // Find parent space
  const findParentSpace = (spaceList, targetId) => {
    for (const space of spaceList) {
      if (space.childrens) {
        for (const child of space.childrens) {
          if (child.id === targetId) {
            return space;
          }
          const found = findParentSpace([child], targetId);
          if (found) return found;
        }
      }
    }
    return null;
  };

  const handleSearch = async () => {
    setIsSearching(true);
    try {
      let url;
      if (orgAllCollectionsMode && organizationId) {
        url = `${API_URL}/spaces/authenticated/organization/collections?organization_id=${organizationId}&minimal_mode=true&size=150`;
        if (searchQuery.trim()) url += `&name=${encodeURIComponent(searchQuery.trim())}`;
      } else if (!searchQuery.trim()) {
        url = `${API_URL}/spaces?minimal_mode=true`;
        if (orgSharedMode && organizationId) {
          url += `&shared=true&organization_id=${organizationId}`;
        } else if (organizationId) {
          url += `&shared=false&organization_id=${organizationId}`;
        } else if (disableDrag) {
          url += '&shared=true';
        } else {
          url += '&shared=false';
        }
      } else {
        url = `${API_URL}/spaces?space_name=${encodeURIComponent(searchQuery.trim())}&minimal_mode=true`;
        if (orgSharedMode && organizationId) url += `&shared=true&organization_id=${organizationId}`;
        else if (organizationId) url += `&organization_id=${organizationId}`;
        else if (!disableDrag) url += '&shared=false';
        else url += '&shared=true';
      }
      const response = await axios.get(url);
      if (onSpacesUpdate) {
        onSpacesUpdate(response.data.spaces || []);
      }
    } catch (error) {
      console.error('Error searching spaces:', error);
    } finally {
      setIsSearching(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleClearSearch = async () => {
    setSearchQuery("");
    setIsSearching(true);
    try {
      let url;
      if (orgAllCollectionsMode && organizationId) {
        url = `${API_URL}/spaces/authenticated/organization/collections?organization_id=${organizationId}&minimal_mode=true&size=150`;
      } else {
        url = `${API_URL}/spaces?minimal_mode=true`;
        if (orgSharedMode && organizationId) {
          url += `&shared=true&organization_id=${organizationId}`;
        } else if (organizationId) {
          url += `&shared=false&organization_id=${organizationId}`;
        } else if (disableDrag) {
          url += '&shared=true';
        } else {
          url += '&shared=false';
        }
      }

      const response = await axios.get(url);
      if (onSpacesUpdate) {
        onSpacesUpdate(response.data.spaces || []);
      }
    } catch (error) {
      console.error('Error clearing search:', error);
    } finally {
      setIsSearching(false);
    }
  };

  // DRAG & DROP EVENT HANDLERS (No changes here)
  const handleDragStart = (e, space) => {
    setDragInProcess(true);
    e.stopPropagation();
    setDraggingSpaceId(space.id);
    setDraggingSpace(space);

    e.dataTransfer.effectAllowed = "move";
    const dragGhost = document.createElement("div");
    dragGhost.textContent = space.name;
    dragGhost.style.position = "absolute";
    dragGhost.style.top = "-9999px";
    dragGhost.style.left = "-9999px";
    dragGhost.style.fontSize = "14px";
    dragGhost.style.fontWeight = "bold";
    dragGhost.style.padding = "4px 8px";
    dragGhost.style.borderRadius = "4px";
    dragGhost.style.backgroundColor = "#fff";
    dragGhost.style.border = "1px solid #ccc";
    dragGhost.style.boxShadow = "0px 2px 5px rgba(0,0,0,0.15)";
    dragGhost.style.pointerEvents = "none";

    document.body.appendChild(dragGhost);
    e.dataTransfer.setDragImage(dragGhost, 0, 0);

    setTimeout(() => {
      document.body.removeChild(dragGhost);
    }, 0);
  };

  // Add rated object to space functionality - now opens AddToSpace dialog
  const handleRatedObjectDrop = (ratedObjectData, spaceId) => {
    if (showSpacePopup) {
      // Pass the rating ID, full item data, and target space ID to the AddToSpace dialog
      showSpacePopup(ratedObjectData.id, ratedObjectData.fullData || ratedObjectData, spaceId);
    } else {
      console.error('showSpacePopup function not provided to SpaceList');
    }
  };

  // Enhanced drag over handler
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Check if this is a rated object being dragged
    const dragData = e.dataTransfer.types.includes("application/json");
    if (dragData && allowRatedObjectDrop) {
      e.dataTransfer.dropEffect = "copy";
    }
  };

  // Enhanced drop handler
  const handleDrop = (e, space) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverSpaceId(null);
    
    try {
      // Check if this is a rated object drop
      const jsonData = e.dataTransfer.getData("application/json");
      if (jsonData && allowRatedObjectDrop) {
        const dragData = JSON.parse(jsonData);
        if (dragData.type === "rated-object") {
          handleRatedObjectDrop(dragData.ratedObject, space.id);
          return;
        }
      }
    } catch (error) {
      console.error('Error parsing drop data:', error);
    }

    // Handle space-to-space drops (existing functionality)
    setDragInProcess(false);
    if (draggingSpaceId === space.id) return;
    setDropDestinationId(space.id);
    setDropDestinationSpace(space);
    setShowActionDialog(true);
  };

  const handleDragEnd = (e) => {
    setDragInProcess(false);
    e.stopPropagation();
  };

  const handleCreateSubspace = (e, space) => {
    e.stopPropagation();
    setIsCreateSpaceOpen(true);
    setIsSubSpaceMode(true);
    
    // Only set parentSpaceId if this is not a top-level space
    const parentSpace = findParentSpace(spaces, space.id);
    if (parentSpace) {
      setParentSpaceId(parentSpace.id);
    } else {
      setParentSpaceId(null);
    }
  };

  // Handle drag enter for visual feedback
  const handleDragEnter = (e, spaceId) => {
    if (allowRatedObjectDrop) {
      e.preventDefault();
      setDragOverSpaceId(spaceId);
    }
  };

  // Handle drag leave for visual feedback
  const handleDragLeave = (e, spaceId) => {
    if (allowRatedObjectDrop) {
      // Only clear if we're actually leaving the space item
      const rect = e.currentTarget.getBoundingClientRect();
      const x = e.clientX;
      const y = e.clientY;
      
      if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
        setDragOverSpaceId(null);
      }
    }
  };

  const renderSpaces = (spaceList, level = 0) => {
    return spaceList.map((space, index) => {
      const hasChildren = space.has_children;
      const isExpanded = !!expandedSpaces[space.id];
      const isActive = location.pathname.includes(`/spaces/${space.id}`);
      const isLoading = loadingSubspaces[space.id];
      const isDragOver = dragOverSpaceId === space.id;
      
      // Calculate available width based on level and other elements
      const baseWidth = 180;
      const levelPadding = level * 24; // 3 * 8px per level
      const expandButtonWidth = open ? 32 : 0; // Folder/expand slot width for all items
      const addButtonWidth = isActive ? 40 : 0;
      const availableWidth = baseWidth - levelPadding - expandButtonWidth;

      return (
        <React.Fragment key={space.id}>
          <ListItemButton
            style={{
              backgroundColor:
                !disableDrag && dragInProcess ? "rgb(181 242 227)" : 
                isDragOver && allowRatedObjectDrop ? "rgba(33, 150, 243, 0.1)" : "",
            }}
            draggable={!disableDrag}
            onDragStart={disableDrag ? undefined : (e) => handleDragStart(e, space)}
            onDragOver={handleDragOver}
            onDrop={(e) => handleDrop(e, space)}
            onDragEnd={disableDrag ? undefined : handleDragEnd}
            onDragEnter={allowRatedObjectDrop ? (e) => handleDragEnter(e, space.id) : undefined}
            onDragLeave={allowRatedObjectDrop ? (e) => handleDragLeave(e, space.id) : undefined}
            sx={{
              justifyContent: open ? "initial" : "center",
              pl: open ? 5 + level * 3 : 2,
              pr: open ? (isActive ? 5 : undefined) : 2,
              position: "relative",
              minHeight: "48px",
              border: isDragOver && allowRatedObjectDrop ? "2px dashed #2196F3" : "2px solid transparent",
              borderRadius: isDragOver ? "4px" : "0px",
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              overflow: "hidden",
              overflowX: "hidden",
            }}
            onClick={() => {
              // Find the parent space before navigating
              const parentSpace = findParentSpace(spaces, space.id);
              if (parentSpace) {
                setParentSpaceId(parentSpace.id);
              } else {
                setParentSpaceId(null);
              }
              // Expand/collapse when clicking row if it has sub-collections
              if (hasChildren) {
                handleSpaceToggle({ stopPropagation: () => {} }, space.id);
              }
              navigate(`/spaces/${space.id}`);
              if (isMobile) setMobileOpen(false);
              setProfile(false);
            }}
          >
            {/* Fixed-width icon slot: all items align, text never shifts */}
            {open && (
              <Box
                sx={{
                  width: 32,
                  minWidth: 32,
                  flexShrink: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {hasChildren ? (
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSpaceToggle(e, space.id);
                    }}
                    sx={{ m: -0.5 }}
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <CircularProgress size={20} />
                    ) : isExpanded ? (
                      <FolderOpen fontSize="small" sx={{ color: isActive ? "rgba(99,102,241,0.9)" : "rgba(30,41,59,0.6)" }} />
                    ) : (
                      <FolderOpen fontSize="small" sx={{ color: isActive ? "rgba(99,102,241,0.9)" : "rgba(71,85,105,0.7)" }} />
                    )}
                  </IconButton>
                ) : (
                  <Folder
                    fontSize="small"
                    sx={{ color: isActive ? "rgba(99,102,241,0.9)" : "rgba(30,41,59,0.6)" }}
                  />
                )}
              </Box>
            )}

            {/* Show folder icon when sidebar collapsed */}
            {!open && (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                width: '100%',
                minWidth: '24px',
                opacity: open ? 0 : 1,
                transition: 'opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              }}>
                {hasChildren ? (
                  <FolderOpen sx={{ 
                    fontSize: 20, 
                    color: isActive ? "rgba(99,102,241,0.9)" : "rgba(30,41,59,0.6)" 
                  }} />
                ) : (
                  <Folder sx={{ 
                    fontSize: 20, 
                    color: isActive ? "rgba(99,102,241,0.9)" : "rgba(30,41,59,0.6)" 
                  }} />
                )}
              </Box>
            )}

            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center',
              justifyContent: 'space-between',
              flex: 1,
              minWidth: 0,
              position: 'relative',
              pr: isActive && open ? '36px' : 0,
              overflow: 'hidden',
              overflowX: 'hidden',
            }}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1,
                flex: 1,
                minWidth: 0,
                overflow: 'hidden',
                overflowX: 'hidden',
              }}>
                {open && (
                  <>
                    <span
                      className={`sidebar-typography sidebar-space-name ${isActive ? 'sidebar-nav-item-active' : ''}`}
                      style={{
                        whiteSpace: "nowrap",
                        textOverflow: "ellipsis",
                        overflow: "hidden",
                        overflowX: "hidden",
                        display: "block",
                        maxWidth: "100%",
                        width: "100%",
                        color: isActive ? "rgba(99,102,241,0.9)" : "rgba(30,41,59,0.8)",
                        opacity: open ? 1 : 0,
                        transition: 'opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      }}
                      title={space.name}
                    >
                      {space.name}
                    </span>
                  </>
                )}
              </Box>
              {isActive && open && (
                <IconButton
                  size="small"
                  onClick={(e) => handleCreateSubspace(e, space)}
                  sx={{ 
                    position: 'absolute',
                    right: 4,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: 32,
                    height: 32,
                    minWidth: 32,
                    zIndex: 1,
                    flexShrink: 0,
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                      transform: 'translateY(-50%)',
                    }
                  }}
                >
                  <AddIcon sx={{ fontSize: 18, color: "rgba(0, 0, 0, 0.6)" }} />
                </IconButton>
              )}
            </Box>
          </ListItemButton>

          {hasChildren && (
            <Collapse in={isExpanded && open} timeout="auto" unmountOnExit>
              <List disablePadding sx={{ overflowX: 'hidden' }}>
                {renderSpaces(space.childrens || [], level + 1)}
              </List>
            </Collapse>
          )}
        </React.Fragment>
      );
    });
  };

  return (
    <Box>
      {!isCurrentSpace && showSearch && (
        <Box
          className="search-box"
          sx={{
            px: 3,
            py: 1,
          }}
        >
          <Box
            sx={{
              backgroundColor: "#f5f5f5",
              borderRadius: "8px",
              padding: "4px 8px",
              boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.1)",
              width: "100%",
              display: "flex",
              alignItems: "center",
            }}
          >
            <InputBase
              placeholder="Search collections..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={handleKeyPress}
              fullWidth
              className="sidebar-typography"
              sx={{ 
                fontSize: "14px",
                color: "#333",
                fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
                fontWeight: 400,
                letterSpacing: "-0.01em"
              }}
            />
            {searchQuery && (
              <IconButton 
                onClick={handleClearSearch}
                disabled={isSearching}
                size="small"
                sx={{ 
                  p: 0.5,
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)'
                  }
                }}
              >
                <ClearIcon sx={{ 
                  fontSize: 18, 
                  color: "rgba(0, 0, 0, 0.6)",
                }} />
              </IconButton>
            )}
            <IconButton 
              onClick={handleSearch}
              disabled={isSearching}
              size="small"
              sx={{ 
                p: 0.5,
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)'
                }
              }}
            >
              {isSearching ? (
                <CircularProgress size={20} />
              ) : (
                <SearchIcon sx={{ 
                  fontSize: 20, 
                  color: "rgba(0, 0, 0, 0.6)",
                }} />
              )}
            </IconButton>
          </Box>
        </Box>
      )}

      {isCurrentSpace && spaces[0]?.parent_space_id && (
        <ListItemButton
          sx={{
            justifyContent: open ? "initial" : "center",
            pl: 5,
            position: "relative",
            color: "rgba(0, 0, 0, 0.6)",
            fontSize: "14px",
          }}
          onClick={() => {
            navigate(`/spaces/${spaces[0].parent_space_id}`);
            if (isMobile) setMobileOpen(false);
            setProfile(false);
          }}
        >
          <Box sx={{ width: 32, minWidth: 32, flexShrink: 0 }} />
          {open && (
            <span
              className="sidebar-typography sidebar-text-sm"
              style={{
                whiteSpace: "nowrap",
                textOverflow: "ellipsis",
                overflow: "hidden",
                display: "inline-block",
                maxWidth: "calc(100% - 5px)",
                color: "rgba(30,41,59,0.7)"
              }}
            >
              ← Back to parent
            </span>
          )}
        </ListItemButton>
      )}

      <Box
        sx={{
          maxHeight: "300px",
          overflowY: "auto",
          overflowX: "hidden",
          // Distinct scrollbar styling for SpaceList (gray theme, thinner, different from main sidebar)
          "&::-webkit-scrollbar": {
            width: "6px",
          },
          "&::-webkit-scrollbar-track": {
            background: "rgba(148, 163, 184, 0.1)",
            borderRadius: "3px",
            margin: "4px 0",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "rgba(71, 85, 105, 0.4)",
            borderRadius: "3px",
            border: "1px solid transparent",
            backgroundClip: "padding-box",
            "&:hover": {
              backgroundColor: "rgba(71, 85, 105, 0.6)",
              backgroundClip: "padding-box",
            },
          },
          scrollbarWidth: "thin",
          scrollbarColor: "rgba(71, 85, 105, 0.4) rgba(148, 163, 184, 0.1)",
        }}
      >
        <List disablePadding sx={{ overflowX: 'hidden' }}>{renderSpaces(spaces)}</List>
      </Box>
    </Box>
  );
};

export default SpaceList;
